import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'context/ToastContext';
import { getCloudflareUploadUrls } from 'api/apiCloudflare';
import { uploadFileWithProgress } from 'utils/helpers/featchFiles/uploadFileWithProgress';
import { runAiJobWithPolling } from 'utils/helpers/ai/runAiJobWithPolling';
import type { LeadCloudFileType, WarehouseComingAiResultType } from '@econtractor/types';
import type { WarehouseAiParsedItem } from './types';

type ImportAiPhase = 'idle' | 'uploading' | 'processing';

interface UseComingAiInfoParams {
    onParsed: (items: WarehouseAiParsedItem[], cloudFile: LeadCloudFileType, fileName: string) => void;
}

const useComingAiInfo = ({ onParsed }: UseComingAiInfoParams) => {
    const { t } = useTranslation(['Warehouses', 'common']);
    const { showToast } = useToast();
    const refInputFile = useRef<HTMLInputElement>(null);
    const [isBusy, setIsBusy] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<number | boolean>(false);
    const [phase, setPhase] = useState<ImportAiPhase>('idle');
    const abortControllerRef = useRef<AbortController | null>(null);

    const processingLabel =
        phase === 'uploading' ? t('common:buttons.uploading') : t('labelAiAnalyzing', { ns: 'Warehouses' });

    const resetInput = () => {
        if (refInputFile.current) refInputFile.current.value = '';
    };

    const handleOpenClick = () => {
        if (isBusy) return;
        refInputFile.current?.click();
    };

    const handleCancel = () => {
        if (phase !== 'processing') return;
        abortControllerRef.current?.abort();
    };

    const handleChangeFile = async () => {
        const file = refInputFile.current?.files?.[0];
        if (!file) return;

        setIsBusy(true);
        setPhase('uploading');
        setUploadProgress(1);

        try {
            const uploadConfigs = await getCloudflareUploadUrls([{ fileName: file.name, contentType: file.type || 'application/octet-stream', fileSize: file.size }]);
            const uploadConfig = uploadConfigs?.[0];
            if (!uploadConfig) throw new Error('cloud_upload_urls_missing');

            const uploaded = await uploadFileWithProgress(file, uploadConfig, setUploadProgress);
            if (!uploaded.success || !uploaded.config?.publicUrl) throw new Error('cloud_upload_failed');

            setUploadProgress(100);
            setPhase('processing');
            abortControllerRef.current = new AbortController();

            const job = await runAiJobWithPolling<{ fileUrl: string; fileName: string }, WarehouseComingAiResultType>({
                requestType: 'warehouses.coming.parse-file',
                payload: {
                    fileUrl: uploaded.config.publicUrl,
                    fileName: file.name,
                },
                pollIntervalMs: 2000,
                timeoutMs: 180000,
                signal: abortControllerRef.current.signal,
            });

            const parsedItems = Array.isArray(job.result?.items) ? job.result.items : [];
            onParsed(parsedItems, uploaded.config, file.name);
            if (parsedItems.length) {
                showToast(t('messAiRowsLoaded', { count: parsedItems.length }), 'success');
            } else {
                showToast(t('messAiRowsEmpty'), 'warning');
            }
        } catch (error) {
            const message = (error as Error)?.message || '';
            if (message === 'ai_job_aborted' || message === 'canceled') {
                showToast(t('messAiCanceled'), 'warning');
            } else {
                console.error(error);
                showToast(t('common:messError'), 'danger');
            }
        } finally {
            abortControllerRef.current = null;
            setIsBusy(false);
            setPhase('idle');
            setUploadProgress(false);
            resetInput();
        }
    };

    return {
        refInputFile,
        isBusy,
        uploadProgress,
        processingLabel,
        phase,
        handleOpenClick,
        handleChangeFile,
        handleCancel,
    };
};

export default useComingAiInfo;
